import { z } from "zod";

export const AGENCY_MAX_LENGTH = 4;
export const ACCOUNT_MAX_LENGTH = 12;
export const DIGIT_MAX_LENGTH = 1;
export const AGENCY_ZERO_VALUE = "0000";

const directDebtFormBaseSchema = z.object({
  agency: z
    .string({ required_error: "Agência é obrigatório" })
    .min(1, "informe o número da agência")
    .max(
      AGENCY_MAX_LENGTH,
      `Agência deve ter no máximo ${AGENCY_MAX_LENGTH} caracteres`,
    )
    .regex(/^\d+$/, "Apenas números são permitidos")
    .refine((value) => value !== AGENCY_ZERO_VALUE, {
      message: `Informe o número da agência`,
    }),
  account: z
    .string({ required_error: "Conta é obrigatório" })
    .min(1, "informe o número da conta")
    .max(
      ACCOUNT_MAX_LENGTH,
      `Conta deve ter no máximo ${ACCOUNT_MAX_LENGTH} caracteres`,
    )
    .regex(/^\d+$/, "Apenas números são permitidos"),
  digit: z
    .string({ required_error: "Dígito é obrigatório" })
    .length(DIGIT_MAX_LENGTH, `Dígito deve ter ${DIGIT_MAX_LENGTH} caractere`)
    .regex(/^[a-zA-Z0-9]$/, "Apenas letras ou números são permitidos"),
  bankData: z.object(
    {
      uuid: z.string({ required_error: "Banco é obrigatório" }),
      number: z.string({ required_error: "Banco é obrigatório" }),
      name: z.string({ required_error: "Banco é obrigatório" }),
    },
    {
      required_error: "Banco é obrigatório",
    },
  ),
  installmentOption: z.object({
    id: z.string({ required_error: "Parcelamento é obrigatório" }),
    label: z.string({ required_error: "Parcelamento é obrigatório" }),
    parcel: z.number({ required_error: "Parcelamento é obrigatório" }),
    parcelAmount: z.number({
      required_error: "Valor da parcela é obrigatório",
    }),
  }),
  billingDay: z.string().optional(),
});

export const directDebtFormSchema = directDebtFormBaseSchema.refine(
  (data) => {
    if (!data.installmentOption) {
      return true;
    }

    return data.installmentOption.parcel === 1 || Boolean(data.billingDay);
  },
  {
    message: "O dia de cobrança é obrigatório",
    path: ["billingDay"],
  },
);

export type DirectDebtFormData = z.infer<typeof directDebtFormSchema>;

export default directDebtFormBaseSchema;

/**
 * Schema for payment method change scenario (card replacement)
 * In this scenario, installmentOption and billingDay are not required
 * because the payment schedule is already defined
 */
export const directDebtPaymentMethodChangeSchema = z.object({
  agency: z
    .string({ required_error: "Agência é obrigatório" })
    .min(1, "informe o número da agência")
    .max(
      AGENCY_MAX_LENGTH,
      `Agência deve ter no máximo ${AGENCY_MAX_LENGTH} caracteres`,
    )
    .regex(/^\d+$/, "Apenas números são permitidos")
    .refine((value) => value !== AGENCY_ZERO_VALUE, {
      message: `Informe o número da agência`,
    }),
  account: z
    .string({ required_error: "Conta é obrigatório" })
    .min(1, "informe o número da conta")
    .max(
      ACCOUNT_MAX_LENGTH,
      `Conta deve ter no máximo ${ACCOUNT_MAX_LENGTH} caracteres`,
    )
    .regex(/^\d+$/, "Apenas números são permitidos"),
  digit: z
    .string({ required_error: "Dígito é obrigatório" })
    .length(DIGIT_MAX_LENGTH, `Dígito deve ter ${DIGIT_MAX_LENGTH} caractere`)
    .regex(/^[a-zA-Z0-9]$/, "Apenas letras ou números são permitidos"),
  bankData: z.object(
    {
      uuid: z.string({ required_error: "Banco é obrigatório" }),
      number: z.string({ required_error: "Banco é obrigatório" }),
      name: z.string({ required_error: "Banco é obrigatório" }),
    },
    {
      required_error: "Banco é obrigatório",
    },
  ),
  installmentOption: z
    .object({
      id: z.string(),
      label: z.string(),
      parcelAmount: z.number().optional(),
    })
    .optional(),
  billingDay: z.string().optional(),
});

export type DirectDebtPaymentMethodChangeFormData = z.infer<
  typeof directDebtPaymentMethodChangeSchema
>;

/**
 * Returns the appropriate schema based on payment method change scenario
 * @param isPaymentMethodChange - true if this is a payment method change (card replacement)
 * @returns The appropriate zod schema for form validation
 */
export const getDirectDebtSchema = (isPaymentMethodChange: boolean) => {
  return isPaymentMethodChange
    ? directDebtPaymentMethodChangeSchema
    : directDebtFormSchema;
};

export const editAccountSchema = z.object({
  agency: z
    .string({ required_error: "Agência é obrigatório" })
    .min(1, "Agência deve ter no mínimo 1 dígito")
    .max(
      AGENCY_MAX_LENGTH,
      `Agência deve ter no máximo ${AGENCY_MAX_LENGTH} dígitos`,
    )
    .regex(/^\d+$/, "Apenas números são permitidos"),
  account: z
    .string({ required_error: "Conta é obrigatório" })
    .min(1, "Conta deve ter no mínimo 1 dígito")
    .max(
      ACCOUNT_MAX_LENGTH,
      `Conta deve ter no máximo ${ACCOUNT_MAX_LENGTH} dígitos`,
    )
    .regex(/^\d+$/, "Apenas números são permitidos"),
  digit: z
    .string({ required_error: "Dígito é obrigatório" })
    .length(DIGIT_MAX_LENGTH, `Dígito deve ter ${DIGIT_MAX_LENGTH} caractere`)
    .regex(/^[a-zA-Z0-9]$/, "Apenas letras ou números são permitidos"),
});

export type EditAccountFormValues = z.infer<typeof editAccountSchema>;
